/**
 * The ONE place a storefront event leaves the page. Pages call `track(name, data)`; what happens
 * next lives here, so adding an ad pixel later (Meta, Google Ads) is one `onTrack(listener)` in this
 * module's callers' startup — not an edit to every page.
 *
 * Today the first-party part: product views and add-to-carts become an anonymous per-day counter
 * on the server (POST /api/shop/view — a handle and an event name, no cookie, no visitor id) that
 * feeds the "wanted online but out of stock" buying advice (worker/src/shop-demand.ts).
 * Searches are counted by the server on `/shop/search?log=1`, so `search` is listeners-only here.
 */
export type TrackName = "view_item" | "search" | "add_to_cart" | "begin_checkout";
export type TrackData = Record<string, unknown>;
type Listener = (name: TrackName, data: TrackData) => void;

const listeners = new Set<Listener>();

/** Subscribe (ad pixels, consent-gated). Returns the unsubscribe. */
export function onTrack(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function track(name: TrackName, data: TrackData = {}): void {
  try {
    const handle = typeof data.handle === "string" ? data.handle : "";
    if (handle && (name === "view_item" || name === "add_to_cart")) {
      void fetch("/api/shop/view", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ handle, event: name === "add_to_cart" ? "add_to_cart" : "view_item" }),
        keepalive: true,
        credentials: "omit",
      }).catch(() => {});
    }
  } catch {
    /* a beacon that cannot be sent is not the shopper's problem */
  }
  for (const fn of listeners) {
    try {
      fn(name, data);
    } catch {
      /* one broken listener must not stop the others */
    }
  }
}
